import classNames from "classnames";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { TableUI } from "components/design/TableUI";
import { MerchantDetailsSchema } from "formValidations/MerchantDetailsSchema";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  taskMerchantApis,
  useUpdateBulkMerchantEmailMutation,
} from "store/apis/taskMerchants";
import { vendorsApi } from "store/apis/vendors";
import { Vendor } from "types/Models/vendors";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { BulkWFormRequest } from "./BulkWFormRequest";

type MerchantDetail = {
  email: string;
  merchant_id: string;
};

type FormValues = {
  merchant_details: MerchantDetail[];
};

const TableRowWithFormik = ({
  index,
  isDisabled,
}: {
  index: number;
  isDisabled: boolean;
}) => {
  return (
    <div>
      <TextInput
        name={`merchant_details.${index}.email`}
        disabled={isDisabled}
      />
    </div>
  );
};

export const ConfirmRowSelect = ({
  isOpen,
  close,
  selectedRows,
  groupId,
  season,
  clearRowSelection,
  someEmailMissing,
}: ModalProps & {
  selectedRows: Vendor[];
  groupId: string;
  season: string;
  clearRowSelection: () => void;
  someEmailMissing: boolean;
}) => {
  const formRequestModal = useModal();
  const [rowSelection, setRowSelection] = useState<Record<number, boolean>>({});
  const [selectedMerchantDetails, setSelectedMerchantDetails] = useState<
    MerchantDetail[]
  >([]);
  const [updateBulkEmails, { isLoading: isUpdating }] =
    useUpdateBulkMerchantEmailMutation();
  const { alertToast, successToast } = useToast();
  const dispatch = useDispatch();
  const [wasUpdated, setWasUpdated] = useState(false);

  const updateMerchant = () => {
    dispatch(vendorsApi.util.invalidateTags(["VENDORS"]));
    dispatch(taskMerchantApis.util.invalidateTags(["Merchants"]));
  };

  useEffect(() => {
    if (isOpen && selectedRows.length > 0) {
      const initialSelection: Record<number, boolean> = {};
      selectedRows.forEach((_, index) => {
        initialSelection[index] = true;
      });
      setRowSelection(initialSelection);
    }
    return () => {
      if (wasUpdated) {
        updateMerchant();
      }
    };
  }, [isOpen, selectedRows.length, wasUpdated, dispatch]);

  const columnHelper = createColumnHelper<Vendor>();

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        size: 1,
        enableSorting: false,
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
            key={row.id}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      }),
      columnHelper.accessor("logo", {
        cell: (info) => {
          const merchantName = info.row.original.name;
          return (
            <div className="t-flex t-gap-2 t-items-center">
              <Avatar
                src={info.getValue()}
                alt={merchantName}
                variant="primary"
                size="regular"
              />
              <button
                className={classNames(
                  "all:unset t-text-subtext t-text-text-60 t-group t-cursor-pointer hover:t-underline hover:t-text-purple-50"
                )}
              >
                {merchantName}
              </button>
            </div>
          );
        },
        header: "VENDOR",
        size: 50,
      }),
      columnHelper.display({
        id: "email",
        cell: ({ row }) => {
          const hasExistingEmail = !!row.original.email;
          return (
            <TableRowWithFormik
              index={row.index}
              isDisabled={hasExistingEmail}
            />
          );
        },
        header: "Email",
        size: 45,
      }),
    ],
    []
  );

  const table = useReactTable({
    data: selectedRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    defaultColumn: {
      minSize: 1,
    },
  });

  const currentlySelectedVendors = table
    .getSelectedRowModel()
    .flatRows.map((row) => row.original);

  const selectedCount = currentlySelectedVendors.length;

  const initialValues: FormValues = {
    merchant_details: selectedRows.map((vendor) => ({
      email: vendor.email || "",
      merchant_id: vendor.uuid,
    })),
  };

  const handleSubmit = async (values: {
    merchant_details: { email: string; merchant_id: string }[];
  }) => {
    try {
      const filteredMerchantDetails = values.merchant_details.filter(
        (_, index) => rowSelection[index]
      );

      const emailChanges = filteredMerchantDetails.filter((merchant) => {
        const originalVendor = selectedRows.find(
          (v) => v.uuid === merchant.merchant_id
        );
        return originalVendor?.email !== merchant.email;
      });

      if (emailChanges.length > 0) {
        await updateBulkEmails({
          groupId,
          payload: { merchants: emailChanges },
        }).unwrap();

        setWasUpdated(true);
        successToast({ message: "Emails added successfully!" });
      }

      setSelectedMerchantDetails(filteredMerchantDetails);
      formRequestModal.open();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const handleSuccess = () => {
    close();
    formRequestModal.close();
    clearRowSelection();
  };

  const hasNoSelectedRows = selectedCount === 0;

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close}>
        <Modal.Content size="large" asChild>
          <FormikForm
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validationSchema={MerchantDetailsSchema}
            validateOnChange={false}
            validateOnBlur
          >
            {({ values, dirty, isValid }) => {
              return (
                <>
                  <Modal.Header>
                    Request W Form
                    <Modal.Close />
                  </Modal.Header>
                  <Modal.Body>
                    <TableUI table={table} />
                  </Modal.Body>
                  <Modal.FooterButtonGroup>
                    <Modal.RawClose asChild>
                      <Button>Cancel</Button>
                    </Modal.RawClose>
                    <Button
                      customType="primary"
                      disabled={hasNoSelectedRows}
                      type="submit"
                      isLoading={isUpdating}
                    >
                      {someEmailMissing
                        ? "Save & Request W Form"
                        : "Request W Form"}
                    </Button>
                  </Modal.FooterButtonGroup>
                </>
              );
            }}
          </FormikForm>
        </Modal.Content>
      </Modal.Root>
      {formRequestModal.isOpen && (
        <BulkWFormRequest
          {...formRequestModal}
          handleSuccess={handleSuccess}
          groupId={groupId}
          merchantDetails={selectedMerchantDetails}
          updateMerchant={updateMerchant}
          season={season}
        />
      )}
    </>
  );
};
